import React, { useState, useRef, useEffect } from "react";
import { Typography } from "@material-ui/core";
import { useSnackbar } from "contexts/SnackbarContext";
import { getFiles, bookmarkFolderApi } from "./FileApi";
import SimpleDialog from "./SimpleDialog";

export default function BookmarkFolder({ folder, reload, closeMenu, className }) {
  const { showSnackbar } = useSnackbar();

  const [bookmarkId, setBookmarkId] = useState(null);
  const [bookmarkModalOpen, setBookmarkModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Use refs to keep references fresh during async operations
  const isProcessingRef = useRef(isProcessing);
  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  const handleBookmarkClick = async (e) => {
    e.stopPropagation();
    if (isProcessingRef.current) return;

    if (closeMenu) closeMenu();
    setIsProcessing(true);
    showSnackbar("Checking files within folder...", "info");

    try {
      // Fetch files within the folder (FileApi.js returns array under res.data)
      const res = await getFiles(null, folder.id);
      const files = res?.data || [];

      // Filter for non-searchable files
      const nonSearchableFiles = files.filter(file => file.is_searchable === false);
      setBookmarkId(folder.id);

      if (nonSearchableFiles.length > 0) {
        // Scans/images found: Show warning modal to the user
        setIsProcessing(false);
        setBookmarkModalOpen(true);
      } else {
        // All files searchable: Proceed immediately with bookmarking
        executeBookmarking(folder.id);
      }
    } catch (error) {
      setIsProcessing(false);
      console.error("Error inspecting folder files:", error);
      showSnackbar(
        error?.response?.data?.error || "Failed to inspect folder contents.",
        "error"
      );
    }
  };

  const executeBookmarking = async (folderId) => {
    setBookmarkModalOpen(false);
    setIsProcessing(true);
    showSnackbar("Merging folder files and generating bookmarks...", "info");

    try {
      await bookmarkFolderApi(folderId);
      showSnackbar("Bookmark completed successfully.", "success");
      if (reload) reload();
    } catch (error) {
      console.error("Error bookmarking index file:", error);
      showSnackbar(
        error?.response?.data?.error || "Failed to generate folder bookmarks.",
        "error"
      );
    } finally {
      setIsProcessing(false);
      setBookmarkId(null);
    }
  };

  const handleCloseModal = () => {
    setBookmarkModalOpen(false);
    setBookmarkId(null);
  };

  return (
    <>
      <Typography
        className={className}
        onClick={handleBookmarkClick}
        style={{
          opacity: isProcessing ? 0.6 : 1,
          pointerEvents: isProcessing ? "none" : "auto",
        }}
      >
        Bookmark
      </Typography>

      {bookmarkModalOpen && (
        <SimpleDialog
          open={bookmarkModalOpen}
          title="Continue Bookmark"
          subTitle="There are some image files in this folder that need to be OCRed. Do you want to proceed with the merging and bookmarking process anyway?"
          handleAgreeBtn={() => executeBookmarking(bookmarkId)}
          closeModal={handleCloseModal}
        />
      )}
    </>
  );
}
